
import { useMutation, useQuery } from '@tanstack/react-query';
import { snowflakeApi } from '../utils/api';
import blobService from './storage/blobService';
import workflowService from './api/workflowService';
import { hasEmptyArrays, ensureProperTopics } from './checkStaleData';

// Define useDbApi utility function (since it's being re-exported)
export const useDbApi = () => {
  // Return whether we should use DB API mode based on config or environment
  return process.env.REACT_APP_USE_DB_API === 'true' || 
         localStorage.getItem('useDbApi') === 'true';
};

// Interface for business configuration
export interface BusinessConfig {
  business_name: string;
  industry: string;
  additional_context?: string;
}

// Interface for keyword analysis request
export interface AnalysisRequest {
  workflow_id: string;
  input_blob: string;
  client_id?: string;
  draft?: boolean;
}

// Interface for analysis status response
export interface AnalysisStatus {
  status: 'created' | 'processing' | 'draft' | 'finalized' | 'failed' | 'rejected';
  message?: string;
  error?: string;
}

// Interface for full analysis response
export interface AnalysisResponse {
  workflow_id: string;
  status: 'created' | 'processing' | 'draft' | 'finalized' | 'failed' | 'rejected' | 'completed';
  message?: string;
  error?: string;
  output_blob?: string;
  stem_words_csv?: string;
  results?: {
    topics: {
      [topic: string]: {
        subtopics: {
          [subtopic: string]: string[]
        }
      }
    }
  }
}

export interface DbAnalysisStatus {
  module_id: number;
  workflow_id: string;
  status: 'created' | 'processing' | 'draft' | 'finalized' | 'failed' | 'rejected' | 'completed';
  module_type: string;
  created_at: string;
  completed_at?: string;
  message?: string;
  error?: string;
  output_blob?: string;
  output_type?: string;
  content_type?: string;
  results?: {
    topics: {
      [topic: string]: {
        subtopics: {
          [subtopic: string]: string[]
        }
      }
    }
  }
}

// Types
export interface HealthCheckResponse {
  status: string;
  checks?: {
    blob_storage: string;
    gemini_api: string;
  };
  timestamp: string;
}

export interface KeywordAnalysisRequest {
  text: string;
  options?: {
    maxKeywords?: number;
    minLength?: number;
    maxLength?: number;
  };
}

export interface KeywordAnalysisResponse {
  keywords: Array<{
    keyword: string;
    score: number;
    context?: string;
  }>;
  metadata: {
    processingTime: number;
    textLength: number;
    options: KeywordAnalysisRequest['options'];
  };
}

export interface ThemeRequest {
  workflow_id: string;
  themes: {
    [theme: string]: string[]
  };
  keywords: string[];
}

export interface ThemeResponse {
  workflow_id: string;
  status: string;
  themes: {
    [theme: string]: string[]
  }
}

export interface VerificationRequest {
  workflow_id: string;
  action: 'finalize' | 'reject';
  feedback?: string;
}

interface AnalysisResult {
  workflowId: string;
  status: 'completed' | 'failed';
  message?: string;
}

// Type definitions
interface StartAnalysisParams {
  workflow_id: string;
  input_blob: string;
  client_id?: string;
  draft: boolean;
  custom_prompt?: string;
}

interface VerifyAnalysisParams {
  workflow_id: string;
  action: 'finalize' | 'reject';
  feedback?: string;
  client_id?: string;
}

// Database-first interfaces
interface DbAnalysisRequest {
  workflowId: number;
  inputBlob: string;
  isDraft?: boolean;
  clientId?: number;
  keywordLimit?: number;
  customPrompt?: string;
}

interface DbAnalysisResponse {
  module_id: number;
  workflow_id: string;
  status: 'created' | 'processing' | 'draft' | 'finalized' | 'failed' | 'rejected';
  message?: string;
  error?: string;
  output_blob?: string;
  stem_words_csv?: string;
  results?: {
    topics: {
      [topic: string]: {
        subtopics: {
          [subtopic: string]: string[]
        }
      }
    }
  }
}

interface DbThemeRequest {
  workflowId: number;
  themes: {
    [theme: string]: string[]
  };
  keywords?: string[];
  clientId?: number;
}

interface DbVerifyRequest {
  workflowId: number;
  action: 'approve' | 'reject';
  feedback?: string;
  clientId?: number;
}

// Service implementation
export class Module1Service {
  private static instance: Module1Service;

  private constructor() {}

  static getInstance(): Module1Service {
    if (!Module1Service.instance) {
      Module1Service.instance = new Module1Service();
    }
    return Module1Service.instance;
  }

  // Create a new business configuration
  public async createBusinessConfig(config: BusinessConfig): Promise<BusinessConfig> {
    console.log('Creating business config with original data:', config);
    
    // Extract client_id from the config object to avoid sending it in the request body
    const { client_id, ...requestData } = config as any;
    
    // Use the client_id as a query parameter
    const params = client_id ? { client_id } : undefined;
    
    console.log('Sending request with:', {
      body: requestData,
      params: params
    });
    
    try {
      const response = await snowflakeApi.post('/api/v1/business-config/', requestData, { params });
      return response.data;
    } catch (error: any) {
      console.error('Failed to create business config:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
      }
      throw error;
    }
  }

  // Get a business configuration by workflow ID
  public async getBusinessConfig(workflowId: string): Promise<BusinessConfig> {
    // MIGRATION: Replace v1 endpoint with db endpoint
    // OLD: /api/v1/business-config/{workflowId}
    // NEW: /api/v1/db/workflow/{workflowId}
    console.log(`Getting business config for workflow ${workflowId}`);
    const response = await snowflakeApi.get(`/api/v1/db/workflow/${workflowId}`);
    return response.data;
  }

  // Start a keyword analysis job
  public async startAnalysis(params: StartAnalysisParams): Promise<AnalysisResponse> {
    console.log('Starting analysis with params:', params);
    
    // Update parameter names to match expected format in the DB-first approach
    // Change from snake_case to camelCase
    const analysisParams = {
      inputBlob: params.input_blob,  // Changed from input_blob to inputBlob
      isDraft: params.draft,         // Changed from draft to isDraft
      ...(params.custom_prompt && { customPrompt: params.custom_prompt })
    };
    
    console.log('Sending request with:', analysisParams);
    
    // Track Gemini API call start in Application Insights
    const appInsights = (window as any).__appInsights;
    const startTime = Date.now();
    const correlationId = `gemini-${params.workflow_id}-${Date.now()}`;
    
    if (appInsights) {
      appInsights.trackEvent({
        name: 'GeminiAPI_CallStarted',
        properties: {
          correlationId,
          workflowId: params.workflow_id,
          clientId: params.client_id || 'default',
          operation: 'startAnalysis',
          timestamp: new Date().toISOString()
        }
      });
    }
    
    try {
      // MIGRATION: Replace v1 endpoint with db endpoint
      // OLD: /api/v1/analyze
      // NEW: /api/v1/db/workflow/{workflow_id}/analyze
      const response = await snowflakeApi.post(`/api/v1/db/workflow/${params.workflow_id}/analyze`, analysisParams);
      console.log('Response from API:', response.data);
      
      // Track successful Gemini API call in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackEvent({
          name: 'GeminiAPI_CallCompleted',
          properties: {
            correlationId,
            workflowId: params.workflow_id,
            clientId: params.client_id || 'default',
            operation: 'startAnalysis',
            responseStatus: response.data?.status || 'unknown',
            durationMs: duration,
            hasResults: !!response.data?.results,
            timestamp: new Date().toISOString()
          }
        });
        
        // Track Gemini response content if available
        if (response.data?.results) {
          appInsights.trackEvent({
            name: 'GeminiAPI_ResponseContent',
            properties: {
              correlationId,
              workflowId: params.workflow_id,
              clientId: params.client_id || 'default',
              operation: 'startAnalysis',
              resultsSummary: JSON.stringify({
                hasTopics: !!response.data.results.topics,
                topicCount: Object.keys(response.data.results.topics || {}).length,
                status: response.data.status
              }),
              timestamp: new Date().toISOString()
            }
          });
        }
      }
      
      return response.data;
    } catch (error) {
      console.error('API error:', error);
      
      // Track Gemini API error in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackException({
          exception: error instanceof Error ? error : new Error('Unknown error in Gemini API call'),
          properties: {
            correlationId,
            workflowId: params.workflow_id,
            clientId: params.client_id || 'default',
            operation: 'startAnalysis',
            durationMs: duration,
            errorType: error instanceof Error ? error.name : typeof error,
            errorMessage: error instanceof Error ? error.message : String(error),
            errorStack: error instanceof Error ? error.stack : undefined,
            timestamp: new Date().toISOString()
          }
        });
      }
      
      throw error;
    }
  }

  // Get analysis status and results
  public async getAnalysisStatus(workflowId: string, clientId: string = 'default'): Promise<AnalysisResponse> {
    console.log(`Getting analysis status for workflow ${workflowId} with client ID ${clientId}`);
    
    // Track Gemini analysis status check in Application Insights
    const appInsights = (window as any).__appInsights;
    const startTime = Date.now();
    const correlationId = `gemini-status-${workflowId}-${Date.now()}`;
    
    if (appInsights) {
      appInsights.trackEvent({
        name: 'GeminiAPI_StatusCheckStarted',
        properties: {
          correlationId,
          workflowId,
          clientId,
          operation: 'getAnalysisStatus',
          timestamp: new Date().toISOString()
        }
      });
    }
    
    try {
      // MIGRATION: Replace v1 endpoint with db endpoint
      // OLD: /api/v1/analyze/{workflow_id} with client_id as query param
      // NEW: /api/v1/db/workflow/{workflow_id}/analyze
      const response = await snowflakeApi.get(`/api/v1/db/workflow/${workflowId}/analyze`);
      
      // Track Gemini status check result in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackEvent({
          name: 'GeminiAPI_StatusCheckCompleted',
          properties: {
            correlationId,
            workflowId,
            clientId,
            operation: 'getAnalysisStatus',
            responseStatus: response.data?.status || 'unknown',
            durationMs: duration,
            hasResults: !!response.data?.results,
            timestamp: new Date().toISOString()
          }
        });
        
        // Log detailed response data for debugging if there's an error
        if (response.data?.error) {
          appInsights.trackException({
            exception: new Error(`Gemini processing error: ${response.data.error}`),
            properties: {
              correlationId,
              workflowId,
              clientId,
              operation: 'getAnalysisStatus',
              responseStatus: response.data.status,
              durationMs: duration,
              errorMessage: response.data.error,
              timestamp: new Date().toISOString()
            }
          });
        }
        
        // Track processing completion events
        if (response.data?.status === 'draft' || response.data?.status === 'finalized') {
          appInsights.trackEvent({
            name: 'GeminiAPI_ProcessingCompleted',
            properties: {
              correlationId,
              workflowId,
              clientId,
              operation: 'getAnalysisStatus',
              status: response.data.status,
              hasResults: !!response.data?.results,
              resultSummary: response.data?.results ? JSON.stringify({
                hasTopics: !!response.data.results.topics,
                topicCount: Object.keys(response.data.results.topics || {}).length
              }) : 'no_results',
              timestamp: new Date().toISOString()
            }
          });
        }
      }
      
      return response.data;
    } catch (error) {
      console.error('Error getting analysis status:', error);
      
      // Track error in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackException({
          exception: error instanceof Error ? error : new Error('Unknown error in status check'),
          properties: {
            correlationId,
            workflowId,
            clientId,
            operation: 'getAnalysisStatus',
            durationMs: duration,
            errorType: error instanceof Error ? error.name : typeof error,
            errorMessage: error instanceof Error ? error.message : String(error),
            timestamp: new Date().toISOString()
          }
        });
      }
      
      throw error;
    }
  }

  // Health check
  public async getHealth(): Promise<HealthCheckResponse> {
    // Remove or comment out the console.log for normal operation
    // console.log('Checking health for Module 1');
    try {
      const response = await snowflakeApi.get('/api/v1/module1/health');
      // Remove or comment out this line to eliminate console spam
      // console.log('Health check response:', response.data);
      return response.data;
    } catch (error) {
      // Keep error logging for actual issues
      console.error('Health check failed:', error);
      throw error;
    }
  }

  // Keyword analysis
  public async analyzeKeywords(text: string, isDraft: boolean, inputBlobPath?: string, clientId: string = 'default'): Promise<AnalysisResult> {
    // Generate a workflow ID if not provided
    const workflowId = `workflow-${Date.now()}`;
    
    // If we have a blob path, use it directly
    if (inputBlobPath) {
      const response = await snowflakeApi.post('/api/v1/analyze', {
        workflow_id: workflowId,
        input_blob: inputBlobPath,
        client_id: clientId,
        draft: isDraft
      });
      return response.data;
    }
    
    // Otherwise, create a temporary file and upload it
    const blob = new Blob([text], { type: 'text/plain' });
    const file = new File([blob], 'input.txt', { type: 'text/plain' });
    
    // Upload the file to blob storage
    const blobPath = await blobService.uploadFileDirectly(file, {
      containerName: 'client-data',
      clientId: clientId,
      workflowId: workflowId,
      dataSource: 'module1-text-input'
    });
    
    // Start the analysis
    const response = await snowflakeApi.post('/api/v1/analyze', {
      workflow_id: workflowId,
      input_blob: blobPath,
      client_id: clientId,
      draft: isDraft
    });
    return response.data;
  }

  // Batch keyword analysis
  public async analyzeKeywordsBatch(requests: AnalysisRequest[]): Promise<AnalysisResponse[]> {
    const response = await snowflakeApi.post('/api/v1/analyze/batch', { requests });
    return response.data;
  }

  async addCustomThemes(request: ThemeRequest): Promise<ThemeResponse> {
    // MIGRATION: Replace v1 endpoint with db endpoint
    // OLD: /api/v1/themes
    // NEW: /api/v1/db/workflow/{workflow_id}/themes
    const workflowId = request.workflow_id;
    
    // Create request without workflow_id since it's in the path
    const themeData = {
      themes: request.themes,
      keywords: request.keywords
    };
    
    const response = await snowflakeApi.post(`/api/v1/db/workflow/${workflowId}/themes`, themeData);
    return response.data;
  }

  async verifyAnalysis(params: VerifyAnalysisParams): Promise<AnalysisResponse> {
    // MIGRATION: Replace v1 endpoint with db endpoint
    // OLD: /api/v1/verify with workflow_id in the body
    // NEW: /api/v1/db/workflow/{workflow_id}/verify
    const workflowId = params.workflow_id;
    
    // Create verification request data without workflow_id
    const requestData = {
      action: params.action,
      feedback: params.feedback
    };
    
    // Use the correct endpoint path with workflow_id in the path
    const response = await snowflakeApi.post(`/api/v1/db/workflow/${workflowId}/verify`, requestData);
    return response.data;
  }

  async downloadResults(workflowId: string, type: 'output' | 'stem_words'): Promise<string> {
    console.log('Downloading results:', { workflowId, type });
    const response = await snowflakeApi.get(`/api/v1/download/${workflowId}`, {
      params: { type }
    });
    return response.data.download_url;
  }

  // Get list of client IDs
  public async getClientIds(): Promise<string[]> {
    // MIGRATION: Replace v1 endpoint with db endpoint
    // OLD: /api/v1/business-config/clients
    // NEW: /api/v1/db/clients
    const response = await snowflakeApi.get('/api/v1/db/clients');
    
    // Check if response.data is an array
    if (Array.isArray(response.data)) {
      return response.data.map((client: any) => client.client_code || client.client_id.toString());
    }
    
    // Check if response.data is an object with a clients property that's an array
    if (response.data && typeof response.data === 'object' && response.data.clients && Array.isArray(response.data.clients)) {
      return response.data.clients.map((client: any) => client.client_code || client.client_id.toString());
    }
    
    // Log error and return empty array to prevent UI crashes
    console.error('Unexpected response format from /api/v1/db/clients:', response.data);
    return [];
  }

  // Get a list of workflows by client ID
  public async getWorkflowsByClientId(clientId: string): Promise<any[]> {
    try {
      // MIGRATION: Replace v1 endpoint with db endpoint
      // OLD: /api/v1/business-config/workflows with client_id as query param
      // NEW: /api/v1/db/client/{clientId}/workflows
      const response = await snowflakeApi.get(`/api/v1/db/client/${clientId}/workflows`);
      return response.data.workflows || [];
    } catch (error) {
      console.error(`Error fetching workflows for client ${clientId}:`, error);
      throw error;
    }
  }

  // DATABASE-FIRST METHODS
  
  // Start a keyword analysis job using database workflow ID
  public async startAnalysisDb(params: DbAnalysisRequest): Promise<DbAnalysisResponse> {
    console.log('Starting analysis with database workflow ID:', params.workflowId);
    
    // Track API call start in Application Insights
    const appInsights = (window as any).__appInsights;
    const startTime = Date.now();
    const correlationId = `db-analysis-${params.workflowId}-${Date.now()}`;
    
    if (appInsights) {
      appInsights.trackEvent({
        name: 'DbAPI_AnalysisStarted',
        properties: {
          correlationId,
          workflowId: params.workflowId,
          clientId: params.clientId,
          operation: 'startAnalysisDb',
          keywordLimit: params.keywordLimit,
          hasCustomPrompt: !!params.customPrompt,
          timestamp: new Date().toISOString()
        }
      });
    }
    
    try {
      // const response = await snowflakeApi.post(`/api/v1/db/workflow/${params.workflow_id}/analyze`, analysisParams);
      // Use the workflowService to call db-first endpoint
      const response = await workflowService.startDbKeywordAnalysis(
        params.workflowId,
        params.inputBlob,
        params.isDraft ?? false,
        params.clientId,
        params.keywordLimit,
        params.customPrompt
      );
      
      console.log('DB-first analysis response:', response);
      
      // If we get a module_id in the response, store it in localStorage
      if (response && response.module_id) {
        // Use our helper method to store the mapping
        this.storeModuleWorkflowMapping(response.module_id, response.workflow_id);
        
        // Store the response in sessionStorage
        const moduleStatusKey = `module_status_${response.module_id}`;
        sessionStorage.setItem(moduleStatusKey, JSON.stringify(response));
      }
      
      // Track successful API call in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackEvent({
          name: 'DbAPI_AnalysisCompleted',
          properties: {
            correlationId,
            workflowId: params.workflowId,
            clientId: params.clientId,
            moduleId: response.module_id,
            operation: 'startAnalysisDb',
            responseStatus: response.status || 'unknown',
            durationMs: duration,
            timestamp: new Date().toISOString()
          }
        });
      }
      
      return response;
    } catch (error) {
      console.error('DB-first API error:', error);
      
      // Track API error in Application Insights
      if (appInsights) {
        const duration = Date.now() - startTime;
        appInsights.trackException({
          exception: error instanceof Error ? error : new Error('Unknown error in DB-first API call'),
          properties: {
            correlationId,
            workflowId: params.workflowId,
            clientId: params.clientId,
            operation: 'startAnalysisDb',
            durationMs: duration,
            errorType: error instanceof Error ? error.name : typeof error,
            errorMessage: error instanceof Error ? error.message : String(error),
            timestamp: new Date().toISOString()
          }
        });
      }
      
      throw error;
    }
  }

  // Get analysis status by workflow ID or module ID
  public async getAnalysisStatusDb(idParam: number, clientId?: number): Promise<any> {
    // Determine if this ID is a module ID or workflow ID based on where it came from
    const isModuleId = localStorage.getItem(`is_module_id_${idParam}`) === 'true';
    
    try {
      if (isModuleId) {
        console.log(`Getting analysis status for module ID ${idParam}`);
        
        // Use the module endpoint directly since we have a module ID
        const response = await workflowService.getDbModuleAnalysisStatus(idParam);
        
        // Store the response in sessionStorage for future reference
        if (response) {
          const moduleStatusKey = `module_status_${idParam}`;
          sessionStorage.setItem(moduleStatusKey, JSON.stringify(response));
          
          // Save/update the mapping if we have the workflow_id in the response
          if (response.workflow_id) {
            this.storeModuleWorkflowMapping(idParam, response.workflow_id);
          }
        }
        
        return response;
      } else {
        console.log(`Getting analysis status for workflow ID ${idParam}`);
        
        // Track API call in Application Insights
        const appInsights = (window as any).__appInsights;
        const startTime = Date.now();
        const correlationId = `db-status-${idParam}-${Date.now()}`;
        
        if (appInsights) {
          appInsights.trackEvent({
            name: 'DbAPI_StatusCheckStarted',
            properties: {
              correlationId,
              workflowId: idParam,
              clientId,
              operation: 'getAnalysisStatusDb',
              timestamp: new Date().toISOString()
            }
          });
        }
        
        try {
          // Use the workflow endpoint for workflow IDs
          const response = await workflowService.getDbKeywordAnalysisStatus(idParam, clientId);
          
          // If the response contains a module ID, store it in sessionStorage for future reference
          if (response && response.module_id) {
            const moduleStatusKey = `module_status_${response.module_id}`;
            sessionStorage.setItem(moduleStatusKey, JSON.stringify(response));
            
            // Also update localStorage mapping
            localStorage.setItem(`module_${response.module_id}_workflow_id`, idParam.toString());
            localStorage.setItem(`is_module_id_${response.module_id}`, 'true');
          }
          
          // Track status check result in Application Insights
          if (appInsights) {
            const duration = Date.now() - startTime;
            appInsights.trackEvent({
              name: 'DbAPI_StatusCheckCompleted',
              properties: {
                correlationId,
                workflowId: idParam,
                clientId,
                operation: 'getAnalysisStatusDb',
                responseStatus: response.status || 'unknown',
                durationMs: duration,
                hasResults: !!response.results,
                timestamp: new Date().toISOString()
              }
            });
          }
          
          return response;
        } catch (error) {
          console.error(`Error getting DB analysis status for workflow ID ${idParam}:`, error);
          
          // Track error in Application Insights
          if (appInsights) {
            const duration = Date.now() - startTime;
            appInsights.trackException({
              exception: error instanceof Error ? error : new Error('Unknown error in DB status check'),
              properties: {
                correlationId,
                workflowId: idParam,
                clientId,
                operation: 'getAnalysisStatusDb',
                durationMs: duration,
                errorType: error instanceof Error ? error.name : typeof error,
                errorMessage: error instanceof Error ? error.message : String(error),
                timestamp: new Date().toISOString()
              }
            });
          }
          
          throw error;
        }
      }
    } catch (error) {
      // Final fallback if all approaches fail
      console.error(`All attempts to get analysis status failed:`, error);
      throw error;
    }
  }

  // Get analysis results by workflow ID
  public async getAnalysisResultsDb(workflowId: number, clientId?: number): Promise<any> {
    console.log(`Getting results for workflow ID ${workflowId}, client ID ${clientId || 'not specified'}`);
    
    try {
      const response = await workflowService.getDbKeywordAnalysisResults(workflowId, clientId);
      return response;
    } catch (error) {
      console.error('Error getting DB analysis results:', error);
      throw error;
    }
  }

  // Get analysis themes by workflow ID
  public async getAnalysisThemesDb(workflowId: number, clientId?: number): Promise<any> {
    console.log(`Getting themes for workflow ID ${workflowId}, client ID ${clientId || 'not specified'}`);
    
    try {
      const response = await workflowService.getDbKeywordAnalysisThemes(workflowId, clientId);
      return response;
    } catch (error) {
      console.error('Error getting DB themes:', error);
      throw error;
    }
  }

  // Add custom themes to a workflow
  public async addCustomThemesDb(request: DbThemeRequest): Promise<any> {
    console.log('Adding custom themes with DB workflow ID:', request.workflowId);
    
    try {
      const response = await workflowService.addDbCustomThemes(
        request.workflowId,
        request.themes,
        request.keywords,
        request.clientId
      );
      return response;
    } catch (error) {
      console.error('Error adding DB custom themes:', error);
      throw error;
    }
  }

  // Verify analysis results for a workflow
  public async verifyAnalysisDb(request: DbVerifyRequest): Promise<any> {
    console.log('Verifying analysis with DB workflow ID:', request.workflowId);
    
    try {
      const response = await workflowService.verifyDbAnalysis(
        request.workflowId,
        request.action,
        request.feedback,
        request.clientId
      );
      return response;
    } catch (error) {
      console.error('Error verifying DB analysis:', error);
      throw error;
    }
  }
  
  // Module ID-based methods
  
  // Get analysis status by module ID
  public async getAnalysisStatusByModuleDb(moduleId: number): Promise<any> {
    console.log(`Getting analysis status for module ID ${moduleId}`);
    
    try {
      const moduleStatusKey = `module_status_${moduleId}`;
      const cachedResponse = sessionStorage.getItem(moduleStatusKey);
      
      // Check if we have cached data with results
      if (cachedResponse) {
        try {
          const parsedResponse = JSON.parse(cachedResponse);
          if (parsedResponse.results) {
            console.log(`Using cached module status with results for module ${moduleId}`);
            return parsedResponse;
          }
        } catch (e) {
          console.warn(`Error parsing cached module status: ${e}`);
          // Continue with fresh fetch
        }
      }
      
      // Get the module status from the database
      const response = await snowflakeApi.get(`/api/v1/db/module/${moduleId}/status`);
      
      // Extract the response data
      const responseData = response.data;
      console.log(`Database analysis status update:`, responseData);
      
      if (!responseData) {
        throw new Error(`No status data for module ${moduleId}`);
      }
      
      // Try to get results if status indicates they should be available
      if (responseData.status === 'completed' || responseData.status === 'draft' || responseData.status === 'finalized') {
        console.log(`${responseData.status} status detected for module ${moduleId}, checking for results`);
        
        // Check if results are already included in the response
        if (responseData.results) {
          console.log(`Results included in the module status response for module ${moduleId}`);
          sessionStorage.setItem(moduleStatusKey, JSON.stringify(responseData));
          return responseData;
        } else {
          console.log(`Completed status detected but no results in response, will try to fetch results separately`);
        }
        
        // Try to get results from the output_blob if available
        if (responseData.output_blob) {
          try {
            console.log(`Module ${moduleId} has output_blob: ${responseData.output_blob}, attempting to fetch blob content`);
            
            // Check if the output_blob contains a path to a directory vs a file
            const pathParts = responseData.output_blob.split('/');
                const workflowsIndex = pathParts.findIndex((part: string) => part === 'workflows' || part === 'workflow');
            let workflowPath;
            
            if (workflowsIndex >= 0 && workflowsIndex + 1 < pathParts.length) {
              // Reconstruct the path up to the module output directory
              const workflowId = pathParts[workflowsIndex + 1];
              workflowPath = pathParts.slice(0, workflowsIndex + 1).join('/') + '/' + workflowId + '/outputs/module1';
              console.log(`Extracted workflow path: ${workflowPath}`);
              
              // Get the latest draft_results file in this directory
              try {
                // Always use the non-db-prefixed endpoint for storage operations
                const apiResponse = await snowflakeApi.get('/api/v1/storage/latest', {
                  params: {
                    path: workflowPath,
                    container: 'client-data',
                    pattern: 'draft_results_*.json',
                    add_client_prefix: false // Don't add client_id prefix to path
                  }
                });
                
                if (apiResponse.data && apiResponse.data.content) {
                  // Update the response with the results
                  responseData.results = apiResponse.data.content;
                  // *** Important fix: also update the status to 'draft' to signal completion ***
                  responseData.status = 'draft';
                  sessionStorage.setItem(moduleStatusKey, JSON.stringify(responseData));
                  console.log(`Successfully retrieved latest results from blob for module ${moduleId}`);
                  return responseData;
                } else {
                  console.warn(`No draft results found in path: ${workflowPath}`);
                }
              } catch (latestError) {
                console.error(`Error fetching latest blob for module ${moduleId}:`, latestError);
              }
            }
            
            // If we couldn't get the latest file, try the specific output_blob
            console.log(`Trying specific output_blob directly: ${responseData.output_blob}`);
            
            // Fix: Remove container prefix if present in the path
            let blobPath = responseData.output_blob;
            if (blobPath.startsWith('client-data/')) {
              blobPath = blobPath.substring('client-data/'.length);
              console.log(`Removing container prefix from path: ${blobPath}`);
            }
            
            // Always use the non-db-prefixed endpoint for storage operations
            const directResponse = await snowflakeApi.get('/api/v1/storage/get', {
              params: {
                path: blobPath,
                container: 'client-data',
                add_client_prefix: false // Don't add client_id prefix to path
              }
            });
            
            if (directResponse.data) {
              // Update the response with the results
              responseData.results = directResponse.data;
              // *** Important fix: also update the status to match completion ***
              responseData.status = 'draft';
              sessionStorage.setItem(moduleStatusKey, JSON.stringify(responseData));
              console.log(`Successfully retrieved direct results from blob for module ${moduleId}`);
              return responseData;
            }
          } catch (blobError) {
            console.error(`Error fetching blob content for module ${moduleId}:`, blobError);
          }
        }
        
        // If we couldn't get results from output_blob, try via workflow ID
        const workflowId = responseData.workflow_id;
        
        if (workflowId) {
          // Try with original format first
          try {
            console.log(`Trying to get results with original workflow ID: ${workflowId}`);
            const resultsResponse = await workflowService.getDbKeywordAnalysisResults(workflowId);
            
            if (resultsResponse && resultsResponse.results) {
              // Update our response with the results
              responseData.results = resultsResponse.results;
              // *** Important fix: also update the status to match completion ***
              responseData.status = 'draft';
              
              // Store the mapping for future use
              this.storeModuleWorkflowMapping(moduleId, workflowId);
              
              // Update storage with the enhanced response
              sessionStorage.setItem(moduleStatusKey, JSON.stringify(responseData));
              console.log(`Successfully retrieved results for module ${moduleId} via workflow ${workflowId}`);
              return responseData;
            }
          } catch (originalError) {
            // If that fails, try with numeric conversion
            try {
              const resultsResponse = await workflowService.getDbKeywordAnalysisResults(Number(workflowId));
              
              if (resultsResponse && resultsResponse.results) {
                // Update our response with the results
                responseData.results = resultsResponse.results;
                // *** Important fix: also update the status to match completion ***
                responseData.status = 'draft';
                
                // Store the mapping for future use
                this.storeModuleWorkflowMapping(moduleId, workflowId);
                
                // Update storage with the enhanced response
                sessionStorage.setItem(moduleStatusKey, JSON.stringify(responseData));
                console.log(`Successfully retrieved results for module ${moduleId} via numeric workflow ${workflowId}`);
                return responseData;
              }
            } catch (numericError) {
              console.warn(`Failed to fetch results in both formats for module ${moduleId}:`, 
                { originalError, numericError });
            }
          }
        } else {
          console.warn(`Module ${moduleId} has no associated workflow ID, cannot fetch results`);
        }
      }
      
      return responseData;
    } catch (error) {
      console.error('Error getting DB analysis status by module ID:', error);
      throw error;
    }
  }

  // Add this new helper method before the closing brace of the class
  private storeModuleWorkflowMapping(moduleId: number, workflowId: string | number): void {
    // Store mapping in localStorage for persistence
    localStorage.setItem(`is_module_id_${moduleId}`, 'true');
    localStorage.setItem(`module_${moduleId}_workflow_id`, workflowId.toString());
    console.log(`Stored mapping: Module ${moduleId} → Workflow ${workflowId}`);
  }

  // Get results directly from a module ID
  public async getModuleResults(moduleId: number): Promise<any> {
    console.log(`Getting results directly for module ID ${moduleId}`);
    
    try {
      const response = await snowflakeApi.get(`/api/v1/db/module/${moduleId}/results`);
      
      if (response.data) {
                    // Store this data for future reference
                    const moduleResultsKey = `module_results_${moduleId}`;
                    
                    // Check if response data is stale (empty keywords/stem_words)
                    // Look for topics with empty arrays
                    let hasEmptyData = false;
                    if (response.data && response.data.topics && Array.isArray(response.data.topics)) {
                        const topics = response.data.topics;
                        topics.forEach((topic: any) => {
                            if (topic.subtopics && Array.isArray(topic.subtopics)) {
                                topic.subtopics.forEach((subtopic: any) => {
                                    // Check for empty arrays - using OR logic to match our fix in checkStaleData.ts
                                    if ((!subtopic.stem_words || subtopic.stem_words.length === 0) ||
                                        (!subtopic.keywords || subtopic.keywords.length === 0)) {
                                        hasEmptyData = true;
                                    }
                                });
                            }
                        });
                    }
                    
                    if (hasEmptyData) {
                        console.warn("Data appears to be stale with empty arrays, marking for refresh");
                        // Don't save this stale data to avoid propagating bad cache
                    } else {
                        sessionStorage.setItem(moduleResultsKey, JSON.stringify(response.data));
                    }
                }
      
      return response.data;
    } catch (error) {
      console.error(`Error getting results for module ${moduleId}:`, error);
      throw error;
    }
  }

  // Get module results using the workflow ID for lookup
  public async getModuleResultsViaWorkflow(moduleId: number): Promise<any> {
    console.log(`Getting module ${moduleId} results via workflow lookup`);
    
    try {
        // First get the module status to find workflow ID
        const moduleStatus = await workflowService.getDbModuleAnalysisStatus(moduleId);
        
        if (!moduleStatus) {
            throw new Error(`Module ${moduleId} not found`);
        }
        
        // First check if the module has an output_blob that might contain results
        if (moduleStatus.output_blob) {
            console.log(`Module ${moduleId} has output_blob: ${moduleStatus.output_blob}, attempting to fetch blob content`);
            
            try {
                // Extract the workflow directory path from output_blob
                const pathParts = moduleStatus.output_blob.split('/');
                let workflowPath;
                
                // Find the "workflows" part in the path
                const workflowsIndex = pathParts.findIndex((part: string) => part === 'workflows');
                if (workflowsIndex >= 0 && workflowsIndex + 1 < pathParts.length) {
                    // Reconstruct the path up to the module output directory
                    const workflowId = pathParts[workflowsIndex + 1];
                    workflowPath = pathParts.slice(0, workflowsIndex + 1).join('/') + '/' + workflowId + '/outputs/module1';
                    console.log(`Extracted workflow path: ${workflowPath}`);
                    
                    // Get the latest draft_results file in this directory
                    try {
                        // Always use the non-db-prefixed endpoint for storage operations
                        const apiResponse = await snowflakeApi.get('/api/v1/storage/latest', {
                            params: {
                                path: workflowPath,
                                container: 'client-data',
                                pattern: 'draft_results_*.json',
                                add_client_prefix: false // Don't add client_id prefix to path
                            }
                        });
                        
                        if (apiResponse.data && apiResponse.data.content) {
                            // Update the response with the results
                            const mergedResponse = {
                                ...moduleStatus,
                                results: apiResponse.data.content
                            };
                            
                            // Cache the data
                            const moduleStatusKey = `module_status_${moduleId}`;
                            sessionStorage.setItem(moduleStatusKey, JSON.stringify(mergedResponse));
                            
                            // If there's a workflow ID, store the mapping
                            if (moduleStatus.workflow_id) {
                                this.storeModuleWorkflowMapping(moduleId, moduleStatus.workflow_id);
                            }
                            
                            console.log(`Successfully retrieved latest results from blob for module ${moduleId}`);
                            return mergedResponse;
                        } else {
                            console.warn(`No draft results found in path: ${workflowPath}`);
                        }
                    } catch (latestError) {
                        console.error(`Error fetching latest blob for module ${moduleId}:`, latestError);
                    }
                }
                
                // If we couldn't get the latest file, try the specific output_blob
                console.log(`Trying specific output_blob directly: ${moduleStatus.output_blob}`);
                
                // Fix: Remove container prefix if present in the path
                let blobPath = moduleStatus.output_blob;
                if (blobPath.startsWith('client-data/')) {
                    blobPath = blobPath.substring('client-data/'.length);
                    console.log(`Removing container prefix from path: ${blobPath}`);
                }
                
                // Always use the non-db-prefixed endpoint for storage operations
                const directResponse = await snowflakeApi.get('/api/v1/storage/get', {
                    params: {
                        path: blobPath,
                        container: 'client-data',
                        add_client_prefix: false // Don't add client_id prefix to path
                    }
                });
                
                if (directResponse.data) {
                    // Create merged response with the results from blob
                    const mergedResponse = {
                        ...moduleStatus,
                        results: directResponse.data
                    };
                    
                    // Cache the data
                    const moduleStatusKey = `module_status_${moduleId}`;
                    sessionStorage.setItem(moduleStatusKey, JSON.stringify(mergedResponse));
                    
                    // If there's a workflow ID, store the mapping
                    if (moduleStatus.workflow_id) {
                        this.storeModuleWorkflowMapping(moduleId, moduleStatus.workflow_id);
                    }
                    
                    console.log(`Successfully retrieved direct results from blob for module ${moduleId}`);
                    return mergedResponse;
                }
            } catch (blobError) {
                console.error(`Error fetching blob content for module ${moduleId}:`, blobError);
                // Continue to workflow approach as fallback
            }
        }
        
        // Extract workflow ID for the traditional approach
        const workflowId = moduleStatus.workflow_id;
        
        if (!workflowId) {
            throw new Error(`No workflow ID found for module ${moduleId}`);
        }
        
        let resultsResponse;
        
        try {
            // First try the workflow ID as-is (could be string/UUID format)
            console.log(`Trying to get results with original workflow ID: ${workflowId}`);
            resultsResponse = await workflowService.getDbKeywordAnalysisResults(workflowId);
        } catch (originalIdError) {
            console.warn(`Failed with original ID format, trying numeric conversion: ${workflowId}`);
            
            try {
                // If original ID fails, try converting to number
                resultsResponse = await workflowService.getDbKeywordAnalysisResults(Number(workflowId));
            } catch (numericIdError) {
                // If both approaches fail, throw a comprehensive error
                console.error(`Failed to get results with both ID formats for module ${moduleId}`);
                throw new Error(`Could not fetch results: Original format error: ${originalIdError}, Numeric format error: ${numericIdError}`);
            }
        }
        
        // Merge the results with module status for complete data
        const mergedResponse = {
            ...moduleStatus,
            results: resultsResponse.results || {}
        };
        
        // Cache the data
        const moduleStatusKey = `module_status_${moduleId}`;
        sessionStorage.setItem(moduleStatusKey, JSON.stringify(mergedResponse));
        
        // Also store the mapping
        this.storeModuleWorkflowMapping(moduleId, workflowId);
        
        return mergedResponse;
    } catch (error) {
        console.error(`Error getting module ${moduleId} results via workflow:`, error);
        throw error;
    }
  }

  // Get draft results directly without transformations using multiple methods
  public async getDraftResultsDirectly(workflowId: string, clientId?: string): Promise<any> {
    console.log(`Getting draft results directly for workflow ${workflowId}`);
    
    try {
      // First try - Standard API approach, but get only the content without transformations
      try {
        // Use the standard DB endpoint first (works in both container and local)
        const apiResponse = await snowflakeApi.get(`/api/v1/db/workflow/${workflowId}/analyze`);
        
        // If response has results directly, use them
        if (apiResponse.data?.results?.topics) {
          console.log('Successfully retrieved results from DB API');
          return apiResponse.data.results;
        }
        
        // If results are nested in content, extract them
        if (apiResponse.data?.results?.content?.topics) {
          console.log('Extracted content from nested results structure');
          return apiResponse.data.results.content;
        }
      } catch (apiError) {
        console.warn('Standard API approach failed, trying storage API', apiError);
      }
      
      // Second try - Direct storage access (better for local development, may fail in containers)
      try {
        // Construct the path to the module1 output directory
        const workflowPath = `${clientId || 'default'}/workflows/${workflowId}/outputs/module1`;
        
        // Get the latest draft_results file in this directory
        const storageResponse = await snowflakeApi.get('/api/v1/storage/latest', {
          params: {
            path: workflowPath,
            container: 'client-data',
            pattern: 'draft_results_*.json',
            add_client_prefix: false // Don't add client_id prefix to path
          }
        });
        
        if (storageResponse.data && storageResponse.data.content) {
          console.log('Successfully retrieved results directly from blob storage');
          return storageResponse.data.content;
        }
      } catch (storageError) {
        console.warn('Storage API approach failed', storageError);
      }
      
      // If both approaches failed, throw an error
      throw new Error(`Could not retrieve results for workflow ${workflowId} using any method`);
    } catch (error) {
      console.error(`All methods to fetch results directly failed:`, error);
      throw error;
    }
  }

  // Get the latest stored analysis results without rerunning the analysis
  public async getLatestResults(clientId?: string, workflowId?: string): Promise<any> {
    console.log(`Retrieving latest stored analysis results for client: ${clientId}, workflow: ${workflowId}`);
    
    try {
      // If we have both clientId and workflowId, use getDraftResultsDirectly for more precise filtering
      if (clientId && workflowId) {
        console.log('Using direct method with client and workflow filter');
        try {
          return await this.getDraftResultsDirectly(workflowId, clientId);
        } catch (directError) {
          console.warn('Direct method failed, falling back to storage search:', directError);
        }
      }
      
      // First, check if we have any recent module statuses in sessionStorage
      const moduleStatusKeys = Object.keys(sessionStorage).filter(key => key.startsWith('module_status_'));
      
      if (moduleStatusKeys.length > 0) {
        // Filter by client and workflow if provided
        let filteredKeys = moduleStatusKeys;
        
        if (clientId || workflowId) {
          filteredKeys = moduleStatusKeys.filter(key => {
            const moduleData = sessionStorage.getItem(key);
            if (!moduleData) return false;
            
            try {
              const parsedData = JSON.parse(moduleData);
              
              // Filter by clientId if provided
              if (clientId && parsedData.client_id && parsedData.client_id !== clientId) {
                return false;
              }
              
              // Filter by workflowId if provided
              if (workflowId && parsedData.workflow_id && String(parsedData.workflow_id) !== String(workflowId)) {
                return false;
              }
              
              return true;
            } catch (parseError) {
              console.warn('Failed to parse module status data:', parseError);
              return false;
            }
          });
        }
        
        // Sort filtered keys by timestamp in the module ID (if available) or use simple string comparison
        filteredKeys.sort((a, b) => {
          // Extract module IDs from keys (format: module_status_{id})
          const idA = a.split('_')[2];
          const idB = b.split('_')[2];
          
          // Try parsing as numbers for chronological sort
          const numA = parseInt(idA);
          const numB = parseInt(idB);
          
          if (!isNaN(numA) && !isNaN(numB)) {
            // Sort numerically in descending order (newest first)
            return numB - numA;
          }
          
          // Fallback to string comparison
          return b.localeCompare(a);
        });
        
        if (filteredKeys.length > 0) {
          // Get the most recent module status data that matches our filters
          const latestModuleKey = filteredKeys[0];
          const latestModuleData = sessionStorage.getItem(latestModuleKey);
          
          if (latestModuleData) {
            const parsedData = JSON.parse(latestModuleData);
            
            // If the status is successful and we have results, return directly
            if (
              parsedData && 
              ['completed', 'draft', 'finalized'].includes(parsedData.status) && 
              parsedData.results
            ) {
              console.log('Found recent filtered results in session storage:', latestModuleKey);
              return parsedData;
            }
            
            // If we have status but no results, try to get results via workflow ID
            if (parsedData && parsedData.workflow_id) {
              console.log(`Found module status but no results, trying to get results for workflow ${parsedData.workflow_id}`);
              
              try {
                // Try with original format first
                const resultsResponse = await workflowService.getDbKeywordAnalysisResults(parsedData.workflow_id);
                
                if (resultsResponse && resultsResponse.results) {
                  // Merge results with the status data
                  const mergedResponse = {
                    ...parsedData,
                    results: resultsResponse.results
                  };
                  
                  // Update storage with the complete data
                  sessionStorage.setItem(latestModuleKey, JSON.stringify(mergedResponse));
                  
                  console.log('Successfully retrieved results for the latest filtered module');
                  return mergedResponse;
                }
              } catch (originalError) {
                console.warn('Error with original workflow ID format, trying numeric:', originalError);
                
                // If that fails, try with numeric conversion
                try {
                  const resultsResponse = await workflowService.getDbKeywordAnalysisResults(Number(parsedData.workflow_id));
                  
                  if (resultsResponse && resultsResponse.results) {
                    // Merge results with the status data
                    const mergedResponse = {
                      ...parsedData,
                      results: resultsResponse.results
                    };
                    
                    // Update storage with the complete data
                    sessionStorage.setItem(latestModuleKey, JSON.stringify(mergedResponse));
                    
                    console.log('Successfully retrieved results with numeric workflow ID');
                    return mergedResponse;
                  }
                } catch (numericError) {
                  console.warn('Error with numeric workflow ID format:', numericError);
                }
              }
            }
            
            // If we have module ID, try to get complete data directly
            if (parsedData && latestModuleKey.includes('module_status_')) {
              const moduleId = parseInt(latestModuleKey.split('_')[2]);
              
              if (!isNaN(moduleId)) {
                console.log(`Trying to get complete data for module ID ${moduleId}`);
                
                try {
                  // Use existing method to get module results via workflow
                  return await this.getModuleResultsViaWorkflow(moduleId);
                } catch (moduleError) {
                  console.warn(`Error getting results via module ${moduleId}:`, moduleError);
                }
              }
            }
          }
        }
      }
      
      // If no module results found, check for any stored workflow results (filter by workflow if provided)
      const workflowStorageKeys = Object.keys(localStorage).filter(key => 
        key.startsWith('module_') && key.includes('_workflow_id')
      );
      
      if (workflowStorageKeys.length > 0) {
        // Filter by workflow ID if provided
        let filteredWorkflowKeys = workflowStorageKeys;
        
        if (workflowId) {
          filteredWorkflowKeys = workflowStorageKeys.filter(key => {
            const storedWorkflowId = localStorage.getItem(key);
            return storedWorkflowId && String(storedWorkflowId) === String(workflowId);
          });
        }
        
        if (filteredWorkflowKeys.length > 0) {
          // Sort to get the latest workflow (assuming module IDs are sequential)
          filteredWorkflowKeys.sort().reverse();
          
          // Get the latest module-workflow mapping
          const latestMappingKey = filteredWorkflowKeys[0];
          const storedWorkflowId = localStorage.getItem(latestMappingKey);
          
          if (storedWorkflowId) {
            // Extract module ID from key (format: module_{id}_workflow_id)
            const moduleIdMatch = latestMappingKey.match(/module_(\d+)_workflow_id/);
            
            if (moduleIdMatch && moduleIdMatch[1]) {
              const moduleId = parseInt(moduleIdMatch[1]);
              
              if (!isNaN(moduleId)) {
                console.log(`Found latest filtered module-workflow mapping: Module ${moduleId} → Workflow ${storedWorkflowId}`);
                
                try {
                  // Use existing method to get module results
                  return await this.getModuleResultsViaWorkflow(moduleId);
                } catch (error) {
                  console.error(`Error retrieving latest results via module ${moduleId}:`, error);
                }
              }
            }
            
            // If we couldn't get results via module ID, try direct workflow access
            try {
              console.log(`Trying to get results directly with workflow ID ${storedWorkflowId}`);
              // Convert string to number to fix type error
              const numericWorkflowId = Number(storedWorkflowId);
              return await this.getAnalysisResultsDb(numericWorkflowId);
            } catch (error) {
              console.error(`Error retrieving latest results via workflow ${storedWorkflowId}:`, error);
            }
          }
        }
      }
      
      const errorMsg = clientId && workflowId 
        ? `No recent analysis results found for client ${clientId} and workflow ${workflowId}`
        : 'No recent analysis results found in storage';
      throw new Error(errorMsg);
    } catch (error) {
      console.error('Error retrieving latest results:', error);
      throw error;
    }
  }
}

// React Query hooks
export const useStartAnalysis = () => {
  return useMutation({
    mutationFn: (params: StartAnalysisParams) => 
      Module1Service.getInstance().startAnalysis(params),
  });
};

export const useGetAnalysisStatus = (workflowId: string, clientId?: string) => {
  return useQuery({
    queryKey: ['analysisStatus', workflowId, clientId],
    queryFn: () => Module1Service.getInstance().getAnalysisStatus(workflowId, clientId),
    enabled: !!workflowId,
    refetchInterval: 15000 // Increased from 10 to 15 seconds for Gemini Pro
  });
};

// Database-first hooks
export const useStartAnalysisDb = () => {
  return useMutation({
    mutationFn: (params: DbAnalysisRequest) => 
      Module1Service.getInstance().startAnalysisDb(params),
  });
};

export const useGetAnalysisStatusDb = (idParam: number, clientId?: number) => {
  // Check if this is a module ID
  const isModuleId = localStorage.getItem(`is_module_id_${idParam}`) === 'true';
  
  return useQuery({
    queryKey: ['dbAnalysisStatus', idParam, clientId, isModuleId],
    queryFn: () => isModuleId 
      ? Module1Service.getInstance().getAnalysisStatusByModuleDb(idParam)
      : Module1Service.getInstance().getAnalysisStatusDb(idParam, clientId),
    enabled: !!idParam,
    refetchInterval: 15000 // Increased from 10 to 15 seconds for Gemini Pro
  });
};

export const useGetAnalysisResultsDb = (workflowId: number, clientId?: number) => {
  return useQuery({
    queryKey: ['dbAnalysisResults', workflowId, clientId],
    queryFn: () => Module1Service.getInstance().getAnalysisResultsDb(workflowId, clientId),
    enabled: !!workflowId
  });
};

export const useGetAnalysisThemesDb = (workflowId: number, clientId?: number) => {
  return useQuery({
    queryKey: ['dbAnalysisThemes', workflowId, clientId],
    queryFn: () => Module1Service.getInstance().getAnalysisThemesDb(workflowId, clientId),
    enabled: !!workflowId
  });
};

export const useAddCustomThemes = () => {
  return useMutation({
    mutationFn: (request: ThemeRequest) => 
      Module1Service.getInstance().addCustomThemes(request),
  });
};

export const useAddCustomThemesDb = () => {
  return useMutation({
    mutationFn: (request: DbThemeRequest) => 
      Module1Service.getInstance().addCustomThemesDb(request),
  });
};

export const useVerifyAnalysis = () => {
  return useMutation({
    mutationFn: (params: VerifyAnalysisParams) => 
      Module1Service.getInstance().verifyAnalysis(params),
  });
};

export const useVerifyAnalysisDb = () => {
  return useMutation({
    mutationFn: (request: DbVerifyRequest) => 
      Module1Service.getInstance().verifyAnalysisDb(request),
  });
};

// Module ID-based hooks
export const useGetAnalysisStatusByModuleDb = (moduleId: number) => {
  return useQuery({
    queryKey: ['dbAnalysisStatusByModule', moduleId],
    queryFn: async () => {
      const response = await Module1Service.getInstance().getAnalysisStatusByModuleDb(moduleId);
      
      // If we have a complete status but no results, try to fetch using workflow ID
      if (response && ['completed', 'draft', 'finalized'].includes(response.status) && !response.results) {
        console.log(`Response status is ${response.status} but no results, attempting to fetch results...`);
        const workflowId = response.workflow_id;
        
        if (workflowId) {
          // Try with original format first
          try {
            const resultsResponse = await workflowService.getDbKeywordAnalysisResults(workflowId);
            if (resultsResponse && resultsResponse.results) {
              response.results = resultsResponse.results;
              // Update status for UI to recognize completion
              response.status = 'draft';
              return response;
            }
          } catch (originalError) {
            // If that fails, try with numeric conversion
            try {
              const resultsResponse = await workflowService.getDbKeywordAnalysisResults(Number(workflowId));
              if (resultsResponse && resultsResponse.results) {
                response.results = resultsResponse.results;
                // Update status for UI to recognize completion
                response.status = 'draft';
                return response;
              }
            } catch (numericError) {
              console.warn(`Failed to fetch results in both formats in hook:`, 
                { originalError, numericError });
            }
          }
        }
      }
      
      // If we have response but still in 'processing' status, let's check if we can find a cached version with results
      if (response && response.status === 'processing') {
        try {
          // Check if we have a cached version with results in sessionStorage
          const moduleStatusKey = `module_status_${moduleId}`;
          const cachedData = sessionStorage.getItem(moduleStatusKey);
          
          if (cachedData) {
            const parsedCache = JSON.parse(cachedData);
            if (parsedCache.results) {
              console.log('Found cached results for module, using them instead of processing status');
              // Return the cached version with results instead of the current processing one
              return parsedCache;
            }
          }
        } catch (cacheError) {
          console.warn('Error checking cached status:', cacheError);
        }
      }
      
      return response;
    },
    enabled: !!moduleId,
    refetchInterval: 15000 // Increased from 10 to 15 seconds for Gemini Pro
  });
};

export const useDownloadResults = () => {
  return useMutation({
    mutationFn: ({ workflowId, type }: { workflowId: string; type: 'output' | 'stem_words' }) =>
      Module1Service.getInstance().downloadResults(workflowId, type),
  });
};

export const useGetModuleResults = (moduleId: number) => {
  return useQuery({
    queryKey: ['dbModuleResults', moduleId],
    queryFn: () => Module1Service.getInstance().getModuleResults(moduleId),
    enabled: !!moduleId,
    // No polling needed for results, they don't change once completed
  });
};

export const useGetModuleResultsViaWorkflow = (moduleId: number) => {
  return useQuery({
    queryKey: ['dbModuleResultsViaWorkflow', moduleId],
    queryFn: () => Module1Service.getInstance().getModuleResultsViaWorkflow(moduleId),
    enabled: !!moduleId,
    // No polling needed for results once they're completed
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // Data considered fresh for 5 minutes
  });
};

// Add a new React Query hook for getting latest results
export const useGetLatestResults = () => {
  return useMutation({
    mutationFn: ({clientId, workflowId}: {clientId?: string, workflowId?: string} = {}) => 
      Module1Service.getInstance().getLatestResults(clientId, workflowId),
  });
};

// Hook to get draft results directly from storage without any transformations
export const useGetDraftResultsDirectly = () => {
  return useMutation({
    mutationFn: ({workflowId, clientId}: {workflowId: string, clientId?: string}) => 
      Module1Service.getInstance().getDraftResultsDirectly(workflowId, clientId),
  });
};

export default Module1Service;
